import React from 'react'
import { cn } from '@scalis/utils'
import { ButtonIcon } from '../button-icon/button-icon'
import { ScrollArea } from '../scroll-area'
import { Select } from '../select'
import { UserInfo } from '../user-info/user-info'
import { Popover } from './components/popover/popover'
import { UserSelectProps } from './user-select.types'

export const UserSelect = ({
  value,
  onChange,
  options,
  renderPopoverItemContent,
  renderListItemContent,
  multiple = true,
  scrollareaClassName,
  onLoadMore,
  onSearch,
  loading,
  ...selectProps
}: UserSelectProps) => {
  const filteredOptions = options.filter((option) => !value.includes(option.value))

  return (
    <div data-testid='user-select'>
      <Select
        {...selectProps}
        options={filteredOptions}
        value={value}
        disabled={selectProps.disabled}
        popover={({ closePopover }) => (
          <Popover
            options={filteredOptions}
            renderItem={(option, index) => {
              const { label, email, profileImage, disabled, icon } = option

              const onClick = () => {
                if (multiple) {
                  onChange([...value, option.value])
                } else {
                  onChange([option.value])
                }
                closePopover()
              }

              const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  if (multiple) {
                    onChange([...value, option.value])
                  } else {
                    onChange([option.value])
                  }
                  closePopover()
                }
              }

              return (
                <div
                  key={`${option.value}-${index}`}
                  role='option'
                  aria-selected={value.includes(option.value)}
                  data-testid='user-select-option'
                  className='w-full rounded-lg p-2 text-left hover:bg-neutral-20 active:bg-neutral-30 cursor-pointer'
                  onClick={disabled ? undefined : onClick}
                  onKeyDown={disabled ? undefined : onKeyDown}
                  tabIndex={0}
                >
                  <UserInfo
                    fullName={label}
                    icon={icon}
                    src={profileImage}
                    email={email}
                    disabled={disabled}
                  >
                    {renderPopoverItemContent?.(option)}
                  </UserInfo>
                </div>
              )
            }}
            filter={(value, { label, email }) =>
              [label, email].some((entry) => entry?.toLowerCase().includes(value.toLowerCase()))
            }
            onLoadMore={onLoadMore}
            onSearch={onSearch}
            loading={loading}
          />
        )}
      />
      {value.length > 0 && (
        <ScrollArea className={cn('max-h-[280px] overflow-auto', scrollareaClassName)}>
          {value.map((id, index) => {
            const option = options.find((option) => option.value === id)

            if (!option) return null

            const { email, profileImage, color } = option

            return (
              <div
                key={`${option.value}-${index}`}
                className='my-1 py-2'
              >
                <UserInfo
                  fullName={option.label}
                  src={profileImage}
                  email={email}
                  color={color}
                >
                  {renderListItemContent?.(option, index)}
                  <ButtonIcon
                    disabled={selectProps.disabled}
                    onClick={() => {
                      onChange(value.filter((id) => id !== option.value))
                    }}
                    className='shrink-0'
                    icon='fa-regular fa-xmark'
                  />
                </UserInfo>
              </div>
            )
          })}
        </ScrollArea>
      )}
    </div>
  )
}
